<script setup lang="ts">
import Close from "~/components/icons/Close.vue";
import Modal from "~/components/modals/Modal.vue";
import Button from "~/components/inputs/Button.vue";
import InputFloatingLabel from "~/components/inputs/InputFloatingLabel.vue";
import SelectDate from "~/components/inputs/SelectDate.vue";
import GenderChoice from "~/components/inputs/GenderChoice.vue";
import AutoComplete from "~/components/inputs/AutoComplete.vue";
import useFocusTrap from "~/composables/useFocusTrap";
import { searchCity, searchState } from "~/utils/searchAutocomplete";

import CF from "codice-fiscale-js";

import { useVuelidate } from "@vuelidate/core";
import cfModalRules from "~/rules/cfModal";
import Validator from "~/utils/validator";

const locale = await getLocale("modals");

defineComponent({
    name: "CfModal",
});

const props = defineProps({
    show: { type: Boolean, required: true },
});

const emit = defineEmits(["close", "closed"]);

const name = useState<string | null>("name", () => null);
const surname = useState<string | null>("surname", () => null);
const taxCode = useState<string | null>("taxCode", () => null);
const gender = useState<string | null>("gender", () => null);
const birthDate = useState<string>("birthDate", () => "");
const birthCountry = useState<string>("birthCountry", () => "ITALIA");
const birthCity = useState<string>("birthCity", () => "");

const placeDisabled = computed(() => {
    return birthCountry.value === "" || birthCountry.value.toUpperCase() !== "ITALIA";
});

const modalWrapper = ref<HTMLElement | null>(null)

const isVisible = computed(() => props.show);

const startYearCfModal = computed(() => {
  const current = new Date().getFullYear();
  return current - MAX_AGE_TO_ASK_FOR_LOAN;
});

useFocusTrap(isVisible, modalWrapper)

const calculateCF = () => {

    const [day, month, year] = birthDate.value.split("/").map(Number);

    let birthplace: string | undefined;
    let birthplaceProvincia: string | undefined;

    if (birthCountry.value.toUpperCase() === "ITALIA") {

        if (birthCity.value.includes("(") && birthCity.value.includes(")")) {

            const start = birthCity.value.indexOf("(");
            const end = birthCity.value.indexOf(")");

            const city = birthCity.value.slice(0, start).trim();
            const province = birthCity.value.slice(start + 1, end).trim();

            birthplaceProvincia = province;
            birthplace = city;

            if (!birthplace) {
                console.error(`Comune non trovato: ${city} (${province})`);
                return;
            }

        } else {

            console.error("Formato birthCity non valido per Italia, serve 'Città (Provincia)'");
            return;

        }

    } else {
        birthplace = birthCountry.value;
        birthplaceProvincia = "";
    }

    const cfParams = {
        name: name.value ?? "",
        surname: surname.value ?? "",
        gender: gender.value as "F" | "M",
        day,
        month,
        year,
        birthplace: birthplace,
        birthplaceProvincia: birthplaceProvincia,
    };

    try {
        const cf = CF.compute(cfParams);
        taxCode.value = cf;
    } catch (err) {
        console.error("Errore calcolo CF:", err);
    }
};

watch(birthCountry, (newVal) => {
  if (newVal?.toUpperCase() !== "ITALIA") {
    birthCity.value = "";
  }
});

const rules = computed(() => {

    if (!props.show) {
        return {};
    }

    return cfModalRules;
});

const v$ = useVuelidate(rules, {
    name,
    surname,
    gender,
    birthDate,
    birthCountry,
    birthCity
});

const validate = () => {
    v$.value.$touch();
    v$.value.$validate();
    return !v$.value.$error;
};

const submit = () => {
    if (!validate()) return;

    calculateCF();

    close();
};

const close = () => {
    emit("close");
};

useHead({
    bodyAttrs: {
        class: computed(() => (props.show ? "overflow-hidden" : "")),
    },
});
</script>

<template>

    <div ref="modalWrapper">
        <Modal
            :show="props.show"
            class="cf-modal"
            main-class="w-screen h-[100dvh]"
            custom-class="w-full overflow-x-scroll sm:overflow-auto"
            @clickOutside="close"
            @closed="emit('closed')"
        >
            <template #title>
                <div class="px-4">
                    <div class="w-full flex justify-end">
                        <button data-testid="close" class="flex items-center justify-end w-4 h-4" @click="close">
                            <Close />
                        </button>
                    </div>
                    <h1 class="pageTitle" style="text-align: center;">
                        {{ locale.cfModal.title }}
                    </h1>
                    <p class="pageSubtitle" style="text-align: center;">
                        {{ locale.cfModal.subtitle }}
                    </p>
                </div>
            </template>

            <template #textV2>
                <form @submit.prevent="submit" class="px-4">
                    <div class="">
                        <div class="mb-4">
                            <GenderChoice
                                v-model="gender"
                                :has-error="v$.gender.$error"
                                :error-message="Validator.message(v$.gender)"
                            />
                        </div>
                        <div class="flex flex-wrap w-full text-left">
                            <div class="w-1/2 pr-2">
                            <!-- <div class="w-full lg:w-1/2 mb-2 lg:mb-0 lg:pr-2"> -->
                                <InputFloatingLabel
                                v-model="name"
                                type="text"
                                :mask="alphaMask"
                                :label="locale.cfModal.name"
                                :error="v$.name.$error"
                                :error-message="Validator.message(v$.name)"
                                test-id="cf-name"
                                />
                            </div>
                            <div class="w-1/2 ">
                                <InputFloatingLabel
                                v-model="surname"
                                type="text"
                                :mask="alphaMask"
                                :label="locale.cfModal.surname"
                                :error="v$.surname.$error"
                                :error-message="Validator.message(v$.surname)"
                                test-id="cf-surname"
                                />
                            </div>
                        </div>
                        <small class="block mb-4 text-left italic">
                        {{ locale.cfModal.nameDisclaimer }}
                    </small>
                    </div>
                    
                    <div class="text-left">
                        <div class="w-full  mb-2 lg:mb-4">
                            <label class="text-xs">{{ locale.cfModal.birthDate }}</label>
                            <SelectDate v-model="birthDate" :label="locale.cfModal.birthDate" :error="v$.birthDate.$error"
                                :error-message="Validator.message(v$.birthDate) || 'null'" show-day show-month show-year
                                test-id="cf-birthdate" :start-year="startYearCfModal"/>
                        </div>
                        <div class="flex flex-wrap w-full">

                            <div class="w-1/2 mb-4 pr-2">
                                <AutoComplete
                                    v-model="birthCountry"
                                    :searchFunction="searchState"
                                    :clear-incomplete="true"
                                    :label="locale.cfModal.birthCountry"
                                    :error="v$.birthCountry.$error"
                                    :error-message="Validator.message(v$.birthCountry)"
                                    test-id="cf-birthcountry"
                                />
                            </div>
                            <div class="w-1/2 mb-0">
                                <AutoComplete
                                    v-model="birthCity"
                                    :disabled="placeDisabled" :searchFunction="searchCity"
                                    :clear-incomplete="true"
                                    :label="locale.cfModal.birthCity"
                                    :error="v$.birthCity.$error"
                                    :error-message="Validator.message(v$.birthCity)"
                                    test-id="cf-birthCity"
                                />
                            </div>
                        </div>

                        <div class="flex flex-col justify-center lg:mb-4">
                            <Button type="submit" class="mx-auto">{{ locale.cfModal.calculate }}</Button>
                        </div>
                    </div>
                </form>
            </template>
        </Modal>
    </div>

</template>

<style lang="css">
.cf-modal {
    justify-content: start;
    flex-direction: column;
    padding: 0 16px;
}
    .cf-modal .modal-wrapper {
        margin: 9px 16px !important;
    }

@media screen and (min-width: 448px){
    .cf-modal .modal-wrapper {
        margin: 90px 16px 20px !important;
    }
}
@media screen and (min-width: 414px) {
    .cf-modal {
        justify-items: center;
    }
}
</style>
